'use client';
import { Phone, ArrowRight, Volume2, VolumeX } from 'lucide-react';
import { useRef, useState } from 'react';
import { useSiteSettings } from '@/lib/CmsContext';
import { resolveDirectMessageLinks } from '@/lib/socialIcons';
import { AppLink } from '@/lib/appLink';

const SOCIAL_HOVER_COLORS = {
  viber: 'hover:bg-purple-600 hover:border-purple-600',
  telegram: 'hover:bg-blue-500 hover:border-blue-500',
  whatsapp: 'hover:bg-green-500 hover:border-green-500',
  instagram: 'hover:bg-pink-500 hover:border-pink-500',
  tiktok: 'hover:bg-foreground hover:border-foreground',
  facebook: 'hover:bg-blue-600 hover:border-blue-600',
  youtube: 'hover:bg-red-600 hover:border-red-600',
};

function trimValue(value) {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * @param {import('@/types/cms').CtaAdvancedPageSection} [section]
 */
export default function CtaAdvanced({ section = {} }) {
  const { socialLinks: cmsSocialLinks, social } = useSiteSettings();
  const directLinks = section.show_social_links === false
    ? []
    : resolveDirectMessageLinks(cmsSocialLinks, social);

  const eyebrow = trimValue(section.eyebrow);
  const headingLine1 = trimValue(section.heading_line1);
  const headingLine2 = trimValue(section.heading_line2);
  const introText = trimValue(section.intro_text);
  const introHighlight = trimValue(section.intro_highlight);
  const bodyText = trimValue(section.body_text);
  const quote = trimValue(section.quote);
  const quoteAuthor = trimValue(section.quote_author);
  const socialLabel = trimValue(section.social_label) || 'Написати напряму';
  const videoUrl = trimValue(section.video_url);
  const primaryLabel = trimValue(section.primary_button_label) || "Зв'язатися";
  const primaryHref = trimValue(section.primary_button_href) || '/contacts';
  const secondaryLabel = trimValue(section.secondary_button_label) || 'Переглянути роботи';
  const secondaryHref = trimValue(section.secondary_button_href) || '/our-works';

  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      videoRef.current.muted = false;
      setMuted(false);
      videoRef.current.play();
      setPlaying(true);
    } else {
      videoRef.current.pause();
      setPlaying(false);
    }
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setMuted(!muted);
  };

  const introParts = introHighlight && introText.includes(introHighlight)
    ? introText.split(introHighlight)
    : null;

  const showHeader = eyebrow || headingLine1 || headingLine2;
  const showIntro = Boolean(introText);
  const showQuote = Boolean(quote);
  const showSecondaryButton = Boolean(secondaryHref);
  const secondaryDisplayLabel = secondaryLabel;

  return (
    <section className="relative bg-background py-20 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-1 bg-gold-gradient" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
        {showHeader ? (
          <div className="text-center mb-14">
            {eyebrow ? (
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-8 h-px bg-gold" />
                <span className="text-gold text-xs font-semibold uppercase tracking-widest">{eyebrow}</span>
                <div className="w-8 h-px bg-gold" />
              </div>
            ) : null}
            {headingLine1 || headingLine2 ? (
              <h2 className="text-3xl sm:text-5xl font-black text-foreground mt-2 mb-4 leading-tight">
                {headingLine1 ? (
                  <>
                    {headingLine1}
                    {headingLine2 ? <br /> : null}
                  </>
                ) : null}
                {headingLine2 ? <span className="text-gold-gradient">{headingLine2}</span> : null}
              </h2>
            ) : null}
            <div className="gold-divider" />
          </div>
        ) : null}

        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20 items-center">
          {videoUrl ? (
            <div className="w-full lg:w-2/5 shrink-0 order-2 lg:order-1 flex justify-center">
              <div className="relative" style={{ width: '360px', height: '360px' }}>
                <div className="absolute inset-0 rounded-full border-4 border-gold/60 animate-pulse" />
                <div className="absolute inset-2 rounded-full overflow-hidden bg-dark shadow-2xl shadow-gold/20 cursor-pointer" onClick={togglePlay}>
                  <video
                    ref={videoRef}
                    src={videoUrl}
                    loop
                    playsInline
                    muted
                    className="w-full h-full object-cover"
                  />
                  {!playing && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <div className="w-16 h-16 rounded-full bg-gold/90 flex items-center justify-center shadow-lg">
                        <svg viewBox="0 0 24 24" fill="#141414" className="w-7 h-7 ml-1"><path d="M8 5v14l11-7z" /></svg>
                      </div>
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  onClick={toggleMute}
                  className="absolute bottom-2 right-2 w-9 h-9 rounded-full bg-card border border-border flex items-center justify-center text-muted-foreground hover:text-gold hover:border-gold transition-colors z-10 shadow-lg"
                >
                  {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                </button>
              </div>
            </div>
          ) : null}

          <div className={`w-full flex flex-col gap-8 order-1 lg:order-2 ${videoUrl ? 'lg:w-3/5' : ''}`}>
            {showIntro || bodyText ? (
              <div>
                {showIntro ? (
                  <p className="text-lg text-muted-foreground leading-relaxed mb-4">
                    {introParts ? (
                      <>
                        {introParts[0]}
                        <span className="text-gold font-semibold">{introHighlight}</span>
                        {introParts[1]}
                      </>
                    ) : (
                      introText
                    )}
                  </p>
                ) : null}
                {bodyText ? (
                  <p className="text-base text-muted-foreground leading-relaxed">{bodyText}</p>
                ) : null}
              </div>
            ) : null}

            {showQuote ? (
              <div className="py-5 border-t border-b border-gold/20 bg-gold/5 px-4 rounded-lg">
                <p className="text-sm text-muted-foreground italic mb-3">
                  &quot;{quote}&quot;
                </p>
                {quoteAuthor ? (
                  <div className="flex items-center gap-2">
                    <div className="w-0.5 h-6 bg-gold" />
                    <span className="text-foreground font-bold text-sm uppercase tracking-wider">
                      {quoteAuthor}
                    </span>
                  </div>
                ) : null}
              </div>
            ) : null}

            {directLinks.length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-widest mb-3 font-semibold">{socialLabel}</p>
                <div className="flex flex-wrap gap-2">
                  {directLinks.map((s) => (
                    <a
                      key={`${s.icon}-${s.url}`}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-center gap-2 border border-border text-muted-foreground px-4 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${SOCIAL_HOVER_COLORS[s.icon] || 'hover:border-gold hover:text-gold'} hover:text-white`}
                    >
                      <s.Icon className="w-5 h-5" />
                      {s.label}
                    </a>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-4 flex-wrap lg:flex-nowrap">
              <AppLink
                href={primaryHref}
                className={`flex items-center justify-center gap-2 bg-gold-gradient text-dark font-bold px-7 py-3.5 rounded hover:opacity-90 transition-opacity text-sm uppercase tracking-wider ${showSecondaryButton ? 'flex-1' : 'w-full sm:w-auto'}`}
              >
                <Phone className="w-4 h-4" />
                {primaryLabel}
              </AppLink>
              {showSecondaryButton ? (
                <AppLink
                  href={secondaryHref}
                  className="flex-1 flex items-center justify-center gap-2 border border-border text-foreground hover:border-gold hover:text-gold font-semibold px-7 py-3.5 rounded transition-colors text-sm uppercase tracking-wider"
                >
                  {secondaryDisplayLabel} <ArrowRight className="w-4 h-4" />
                </AppLink>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
